import { PROVIDER_CONNECTOR_TYPE } from '../../types/api/dashboard/DashboardLayout';
import type { DashboardLayoutBlob, DashboardLayoutComponent, DashboardProviderConnector } from '../../types/api/dashboard/DashboardLayout';
import { dashboardQueryParams, isDashboardQueryKey } from '../../types/api/dashboard/DashboardQueryCatalog';
import type { DashboardQueryKey } from '../../types/api/dashboard/DashboardQueryCatalog';
import { isRecord } from '../object';

export type CompileResult = {
    layout: DashboardLayoutBlob;
    problems: string[];
};

/** What the running app knows that a stored layout cannot state for itself */
export type CompileContext = {
    /** Component type keys the platform registers. */
    componentTypes: readonly string[];
    /** Widget keys in the pool.*/
    widgetKeys: readonly string[];
};

/** Rewrites a board's data pool from its widgets' authored bindings. */
export function compileDataConnectors(layout: DashboardLayoutBlob, context?: CompileContext): CompileResult {
    const problems: string[] = [];
    const components = layout.components ?? [];

    const providerConnectors = new Map<string, DashboardProviderConnector>();
    const rewrittenComponents = components.map((component) => {
        if (context !== undefined) {
            checkAgainstRegistries(component, context, problems);
        }

        const authoredKey = authoredQueryKey(component);
        if (authoredKey === undefined) {
            return withoutConnector(component);
        }

        if (typeof authoredKey !== 'string') {
            problems.push(`${describeComponent(component)}: its query key is not text`);
            return withoutConnector(component);
        }

        if (!isDashboardQueryKey(authoredKey)) {
            problems.push(`${describeComponent(component)}: '${authoredKey}' is not a query this platform runs`);
            return withoutConnector(component);
        }

        const queryParams = parseQueryParams(component.queryParams, authoredKey);
        if (queryParams === 'invalid') {
            problems.push(`${describeComponent(component)}: query parameters are not a set of named values`);
            return withoutConnector(component);
        }

        const connectorId = deriveConnectorId(authoredKey, queryParams);
        providerConnectors.set(connectorId, {
            id: connectorId,
            type: PROVIDER_CONNECTOR_TYPE,
            queryKey: authoredKey,
            ...(queryParams === undefined ? {} : { params: queryParams }),
        });
        return { ...component, queryKey: authoredKey, connector: { id: connectorId } };
    });

    return {
        layout: {
            ...layout,
            dataPool: { ...layout.dataPool, connectors: [...providerConnectors.values()] },
            components: rewrittenComponents,
        },
        problems,
    };
}

export type DashboardQueryBindingFields = {
    queryKey?: unknown;
    connector?: unknown;
};

/** Which query a component asks for, from its compiled connector or its authored field. */
export function authoredQueryKey(binding: DashboardQueryBindingFields): unknown {
    const connectorId: unknown = isRecord(binding.connector) ? binding.connector['id'] : undefined;
    if (isDashboardQueryKey(connectorId)) {
        return connectorId;
    }
    return binding.queryKey === '' ? undefined : binding.queryKey;
}

function withoutConnector(component: DashboardLayoutComponent): DashboardLayoutComponent {
    if (component.connector === undefined) {
        return component;
    }
    const unbound = { ...component };
    delete unbound.connector;
    return unbound;
}

/**
 * The parameter values a widget stored, held to the parameters its query declares.
 *
 * Returns `undefined` when the widget passes none and `'invalid'` when what it stored is not a set
 * of named values. Values pass through as written, because the query's own schema on the server is
 * the one declaration that decides what is valid.
 */
function parseQueryParams(stored: unknown, queryKey: DashboardQueryKey): Record<string, unknown> | undefined | 'invalid' {
    if (stored === undefined) {
        return undefined;
    }
    return isRecord(stored) ? declaredParams(stored, queryKey) : 'invalid';
}

/** Keeps only the parameters the query declares, and only those the author filled in. */
function declaredParams(stored: Record<string, unknown>, queryKey: DashboardQueryKey): Record<string, unknown> | undefined {
    const declared = new Set(Object.keys(dashboardQueryParams(queryKey)));
    const filled = Object.entries(stored).filter(([name, value]) => declared.has(name) && value !== '' && value !== undefined && value !== null);
    return filled.length === 0 ? undefined : Object.fromEntries(filled);
}

function deriveConnectorId(queryKey: DashboardQueryKey, queryParams: Record<string, unknown> | undefined): string {
    if (queryParams === undefined) {
        return queryKey;
    }

    const sortedParams = Object.entries(queryParams).sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0));
    return `${queryKey}-${JSON.stringify(Object.fromEntries(sortedParams))}`;
}

/** Holds one component to what this build can render: a type with a renderer, and a widget the pool declares. */
function checkAgainstRegistries(component: DashboardLayoutComponent, context: CompileContext, problems: string[]): void {
    const type = component.type;
    if (type === undefined) {
        problems.push(`${describeComponent(component)} names no component type`);
    } else if (!context.componentTypes.includes(type)) {
        problems.push(`${describeComponent(component)}: '${type}' is not a component type this platform renders`);
    }

    const widgetKey = component.widgetKey;
    if (widgetKey !== undefined && !context.widgetKeys.includes(widgetKey)) {
        problems.push(`${describeComponent(component)}: '${widgetKey}' is not a widget in the pool`);
    }
}

export function describeComponent(component: DashboardLayoutComponent): string {
    return component.renderTo === undefined ? 'an unplaced widget' : `cell '${component.renderTo}'`;
}
